import * as React from 'react';
import getSelectedTableViewId from 'mini-mail-list-store/lib/utils/getSelectedTableViewId';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ListViewActions from 'mini-mail-list-view/lib/schema/ListViewActions';
import MailTriageAction from 'owa-service/lib/contract/MailTriageAction';
import onListViewActionSelected from 'mini-mail-list-view/lib/utils/onListViewActionSelected';
import strings from 'owa-strings/owa-mini-strings';
import toggleMoveToFolderPane from 'mini-mail-folder-store/lib/actions/toggleMoveToFolderPane';
import { deleteFromListView } from 'mini-mail-list-view/lib/utils/deleteFromListView';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { getPropertyIconStyle } from '../utils/getPropertyIconStyle';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { lazyArchiveRow } from 'mini-mail-triage-actions';
import { MailIcons } from 'mini-mail-icons';
import { MiniIcons } from 'owa-mini-icons';
import { PropertyIcons } from '../schema/PropertyIcons';
import { selectlistviewTriageItem } from 'mini-mail-list-actions';

// Remark: We have to share css file with MailListItem since we need to trigger child hover selectors
// off of the MailListItem container css class
const styles = require('./MailListItemIconBar.scss');
const classNames = require('classnames/bind');
const MaxPropertyIconsToDisplay = 3;

export interface MailListItemIconBarProps {
    isFlagged: boolean;
    isPinned: boolean;
    unreadCount: number;
    propertyIcons: PropertyIcons[];
    messageClassIcon: PropertyIcons;
    className?: string;
    rowId?: string;
}

/*
Renders the bar for both action icons and property icons
*/
export class MailListItemIconBar extends React.Component<MailListItemIconBarProps, {}> {
    private renderPropertyIconElements(): JSX.Element[] {
        const propertyIconElements: JSX.Element[] = [];
        const commonPropertyIconCss = classNames(
            styles.icon,
            styles['icon--action'],
            'action-icon'
        );

        const triageActionsOnHover =
            getUserConfiguration()?.ViewStateConfiguration?.MailTriageOnHoverActions || [];
        const { isPinned, isFlagged, unreadCount, rowId } = this.props;
        const isUnread = unreadCount > 0;
        if (triageActionsOnHover.includes(MailTriageAction.Delete)) {
            const deleteImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(MiniIcons.Delete)
            );
            propertyIconElements.push(
                <span
                    tabIndex={-1}
                    key="deleteIcon"
                    role="button"
                    data-type="action"
                    title={strings.readingPaneHeaderDeleteAriaLabel}
                    aria-label={strings.readingPaneHeaderDeleteAriaLabel}
                    className={deleteImageClassName}
                    onClick={this.handleDeleteAction(rowId)}
                    onKeyDown={enterKeyHandler(this.handleDeleteAction(rowId))}
                />
            );
        }

        if (triageActionsOnHover.includes(MailTriageAction.Archive)) {
            const archiveImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(MiniIcons.Archive)
            );
            propertyIconElements.push(
                <span
                    tabIndex={-1}
                    key="archiveButton"
                    role="button"
                    data-type="action"
                    title={strings.readingPaneHeaderArchiveAriaLabel}
                    aria-label={strings.readingPaneHeaderArchiveAriaLabel}
                    className={archiveImageClassName}
                    onClick={this.handleArchiveAction(rowId)}
                    onKeyDown={enterKeyHandler(this.handleArchiveAction(rowId))}
                />
            );
        }

        if (triageActionsOnHover.includes(MailTriageAction.Move)) {
            const moveToImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(MailIcons.FabricMovetoFolder)
            );
            propertyIconElements.push(
                <span
                    tabIndex={-1}
                    key="moveToButton"
                    role="button"
                    data-type="action"
                    title={strings.moveToFolder}
                    aria-label={strings.moveToFolder}
                    className={moveToImageClassName}
                    onClick={this.handleMoveAction(rowId)}
                    onKeyDown={enterKeyHandler(this.handleMoveAction(rowId))}
                />
            );
        }

        if (triageActionsOnHover.includes(MailTriageAction.ReadUnread)) {
            const readImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(isUnread ? MailIcons.Read : MiniIcons.Mail)
            );
            propertyIconElements.push(
                <span
                    tabIndex={-1}
                    role="button"
                    key="readIcon"
                    data-type="action"
                    title={isUnread ? strings.markAsRead : strings.markAsUnread}
                    aria-label={isUnread ? strings.markAsRead : strings.markAsUnread}
                    className={readImageClassName}
                    onKeyDown={enterKeyHandler(this.handleReadAction(isUnread, rowId))}
                    onClick={this.handleReadAction(isUnread, rowId)}
                />
            );
        }

        if (triageActionsOnHover.includes(MailTriageAction.FlagUnflag)) {
            const flagImageClassNames = classNames(
                commonPropertyIconCss,
                getIconClassName(MailIcons.Flag),
                isFlagged ? styles['icon--red'] : undefined
            );
            propertyIconElements.push(
                <span
                    tabIndex={-1}
                    role="button"
                    key="flagIcon"
                    data-type="action"
                    title={isFlagged ? strings.unflag : strings.flag}
                    aria-label={isFlagged ? strings.unflag : strings.flag}
                    className={flagImageClassNames}
                    onKeyDown={enterKeyHandler(this.handleFlagAction(isFlagged, rowId))}
                    onClick={this.handleFlagAction(isFlagged, rowId)}
                />
            );
        }

        if (triageActionsOnHover.includes(MailTriageAction.PinUnpin)) {
            const pinnedImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(isPinned ? MailIcons.PinnedSolid : MailIcons.Pinned),
                isPinned ? styles['icon--themeColor'] : undefined
            );
            propertyIconElements.push(
                <span
                    role="button"
                    key="pinnedIcon"
                    data-type="action"
                    title={isPinned ? strings.unpinText : strings.pinText}
                    aria-label={isPinned ? strings.unpinText : strings.pinText}
                    className={pinnedImageClassName}
                    onKeyDown={enterKeyHandler(this.handlePinAction(isPinned, rowId))}
                    onClick={this.handlePinAction(isPinned, rowId)}
                />
            );
        }

        return propertyIconElements;
    }

    private handleDeleteAction(rowId: string): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            deleteFromListView(rowId);
        };
    }

    private handleArchiveAction(rowId: string): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            lazyArchiveRow(rowId, getSelectedTableViewId());
        };
    }

    private handleMoveAction(rowId: string): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            selectlistviewTriageItem(rowId);
            toggleMoveToFolderPane();
        };
    }

    private handleReadAction(
        isUnread: boolean,
        rowId: string
    ): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            onListViewActionSelected(
                isUnread ? ListViewActions.MarkAsRead : ListViewActions.MarkAsUnread,
                rowId
            );
        };
    }

    private handleFlagAction(
        isFlagged: boolean,
        rowId: string
    ): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            onListViewActionSelected(
                isFlagged ? ListViewActions.Unflag : ListViewActions.Flag,
                rowId
            );
        };
    }

    private handlePinAction(
        isPinned: boolean,
        rowId: string
    ): React.MouseEventHandler<HTMLSpanElement> {
        return ev => {
            ev.stopPropagation();
            onListViewActionSelected(isPinned ? ListViewActions.Unpin : ListViewActions.Pin, rowId);
        };
    }

    private renderReadOnlyPropertyIconElements(): JSX.Element[] {
        const propertyIcons: PropertyIcons[] = this.props.propertyIcons;
        const propertyIconElements: JSX.Element[] = [];
        const addMessageClassIcon =
            this.props.messageClassIcon && this.props.messageClassIcon !== PropertyIcons.None;

        const maxPropertiesToDisplay = Math.min(
            addMessageClassIcon ? propertyIcons.length + 1 : propertyIcons.length,
            MaxPropertyIconsToDisplay
        );

        const commonPropertyIconCss = classNames(styles.icon);
        let propertyIconsAdded = 0;

        // If message class icon is present then always want to display it.
        if (addMessageClassIcon) {
            propertyIconsAdded++;

            const messageClassIconStyles = classNames(
                commonPropertyIconCss,
                getPropertyIconStyle(this.props.messageClassIcon)
            );

            propertyIconElements.push(
                <span
                    data-type="read-only-icon"
                    key={this.props.messageClassIcon.toString()}
                    className={messageClassIconStyles}
                />
            );
        }

        // Add other class icons
        for (let i = 0; i < maxPropertiesToDisplay - propertyIconsAdded; i++) {
            const propertyIcon = propertyIcons[i];
            let propertyIconStyle = classNames(
                commonPropertyIconCss,
                getPropertyIconStyle(propertyIcon)
            );

            if (propertyIcon === PropertyIcons.ImportanceHigh) {
                propertyIconStyle = classNames(propertyIconStyle, styles['icon--red']);
            }

            if (isMiniFeatureEnabled('mini-at-mentions') && this.props.unreadCount > 0) {
                if (propertyIcon === PropertyIcons.GlobalMentionedMe) {
                    propertyIconStyle = classNames(
                        propertyIconStyle,
                        styles['icon--themeDarkColor']
                    );
                }
            }
            propertyIconElements.push(
                <span
                    data-type="read-only-icon"
                    key={propertyIcon.toString()}
                    className={propertyIconStyle}
                />
            );
        }

        if (this.props.isFlagged) {
            const flagImageClassNames = classNames(
                commonPropertyIconCss,
                getIconClassName(MailIcons.Flag),
                styles['icon--red']
            );
            propertyIconElements.push(
                <span key="flagIcon" data-type="read-only-icon" className={flagImageClassNames} />
            );
        }

        if (this.props.isPinned) {
            const pinnedImageClassName = classNames(
                commonPropertyIconCss,
                getIconClassName(MailIcons.PinnedSolid),
                styles['icon--themeColor']
            );
            propertyIconElements.push(
                <span
                    key="pinnedIcon"
                    data-type="read-only-icon"
                    className={pinnedImageClassName}
                />
            );
        }

        return propertyIconElements;
    }

    render() {
        const propertyIconsElement = this.renderPropertyIconElements() || [];
        const readOnlyPropertyIconsElement = this.renderReadOnlyPropertyIconElements() || [];
        const shouldShowIconBar =
            readOnlyPropertyIconsElement.length || propertyIconsElement.length;
        return shouldShowIconBar ? (
            <div className={classNames(styles.iconWrapper, this.props.className)}>
                {propertyIconsElement}
                {readOnlyPropertyIconsElement}
            </div>
        ) : null;
    }
}
